<template>
  <a-modal
    :visible="visible"
    title="创建原料组合包"
    width="800px"
    :maskClosable="false"
    :confirmLoading="loading"
    :okText="'确定'"
    :cancelText="'取消'"
    :okButtonProps="{ disabled: !canSubmit }"
    @ok="handleOk"
    @cancel="handleCancel"
  >
    <div class="create-raw-bag-container">
      <!-- 信息提示 -->
      <a-alert
        type="info"
        show-icon
        :message="alertMessage"
        style="margin-bottom: 16px"
      />

      <!-- 原料包名称 -->
      <a-form-item
        :label-col="{ span: 3 }"
        :wrapper-col="{ span: 6 }"
        label-align="left"
        class="bag-name-form-item"
        :validate-status="nameError ? 'error' : ''"
        :help="nameError"
      >
        <template slot="label">
          <span style="color: red;">*</span>原料包名称
        </template>
        <a-input
          v-model="formData.bagName"
          placeholder="输入原料包名称"
          :maxLength="20"
          class="bag-name-input"
          size="middle"
          @blur="validateBagName"
        />
      </a-form-item>

      <!-- 原料列表表格 -->
      <a-table
        :columns="columns"
        :dataSource="rawList"
        :pagination="false"
        :rowKey="(record, index) => index"
        :rowSelection="{
          type: 'radio',
          selectedRowKeys: [selectedRowKey],
          onChange: handleSelectChange,
          columnTitle: '主原料'
        }"
        size="small"
        bordered
      >
        <template slot="ratio" slot-scope="text, record, index">
          <a-input-number
            v-model="record.ratio"
            :min="0"
            :max="100"
            :precision="2"
            :step="0.01"
            style="width: 100%"
            @change="(val) => handleRatioChange(val, index)"
            @blur="validateRatio(index)"
          />
          <span v-if="ratioErrors[index]" style="color: #ff4d4f; font-size: 12px; display: block;">
            {{ ratioErrors[index] }}
          </span>
        </template>
        <template slot="action" slot-scope="text, record, index">
          <a-button type="link" danger size="small" @click="handleDelete(index)">
            删除
          </a-button>
        </template>
      </a-table>

      <!-- 比例总和提示 -->
      <div class="ratio-summary" style="margin-top: 12px">
        <span>比例总和: </span>
        <span :style="{ color: totalRatio === 100 ? '#52c41a' : '#ff4d4f', fontWeight: 'bold' }">
          {{ totalRatio.toFixed(2) }}%
        </span>
        <span v-if="totalRatio !== 100" style="color: #ff4d4f; margin-left: 8px">
          (比例总和必须等于100%)
        </span>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { post } from '@/Utils/axios'

export default {
  name: 'CreateRawBag',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    selectedRaws: {
      type: Array,
      default: () => []
    },
    formulaId: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      formData: {
        bagName: ''
      },
      rawList: [],
      selectedRowKey: 0, // 默认选中第一行
      nameError: '',
      ratioErrors: {},
      alertMessage: '组合包创建后:在配合饲料中维护原料约束时,将根据组合包中主副原料用量比例,进行副原料用量约束更新;原料数量≤3个。',
      columns: [
        {
          title: '原料',
          dataIndex: 'rawName',
          key: 'rawName',
          width: 200
        },
        {
          title: '代码',
          dataIndex: 'code',
          key: 'code',
          width: 150
        },
        {
          title: '比例(%)',
          dataIndex: 'ratio',
          key: 'ratio',
          width: 150,
          scopedSlots: { customRender: 'ratio' }
        },
        {
          title: '操作',
          key: 'action',
          width: 100,
          align: 'center',
          scopedSlots: { customRender: 'action' }
        }
      ]
    }
  },
  computed: {
    totalRatio() {
      return this.rawList.reduce((sum, item) => {
        return sum + (Number(item.ratio) || 0)
      }, 0)
    },
    canSubmit() {
      // 验证条件：名称不为空、至少2个原料、最多3个原料、比例总和为100%
      return (
        this.formData.bagName.trim() &&
        !this.nameError &&
        this.rawList.length >= 2 &&
        this.rawList.length <= 3 &&
        this.totalRatio === 100 &&
        Object.keys(this.ratioErrors).length === 0
      )
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.initForm()
      }
    },
    selectedRaws: {
      handler(val) {
        if (val && val.length > 0) {
          this.initRawList(val)
        }
      },
      immediate: true
    }
  },
  methods: {
    initForm() {
      this.formData.bagName = ''
      this.nameError = ''
      this.ratioErrors = {}
      // 如果传入了选中的原料，初始化列表
      if (this.selectedRaws && this.selectedRaws.length > 0) {
        this.initRawList(this.selectedRaws)
      } else {
        this.rawList = []
      }
    },

    initRawList(raws) {
      // 限制最多3个原料（如果传入超过3个，只取前3个，但这种情况应该不会发生，因为已经在 openCreateRawBag 中验证了）
      const limitedRaws = raws.slice(0, 3)
      this.rawList = limitedRaws.map((raw, index) => {
        // 如果只有一个原料，默认100%，否则平均分配
        const defaultRatio = limitedRaws.length === 1 
          ? 100 
          : (index === limitedRaws.length - 1 
              ? 100 - (limitedRaws.length - 1) * (100 / limitedRaws.length)
              : 100 / limitedRaws.length)
        
        // 兼容不同的字段名
        return {
          rawId: raw.rawId || raw.id,
          rawName: raw.rawName || raw.name || '',
          code: raw.code || raw.rawCode || '',
          ratio: Number(defaultRatio.toFixed(2))
        }
      })
      // 默认选中第一行
      this.selectedRowKey = 0
    },

    handleSelectChange(selectedRowKeys) {
      // 单选时，selectedRowKeys 是数组，取第一个元素
      this.selectedRowKey = selectedRowKeys[0]
    },

    validateBagName() {
      if (!this.formData.bagName.trim()) {
        this.nameError = '请输入原料包名称'
        return false
      }
      if (this.formData.bagName.length > 20) {
        this.nameError = '原料包名称不能超过20个字符'
        return false
      }
      this.nameError = ''
      return true
    },

    handleRatioChange(val, index) {
      if (val === null || val === undefined) {
        this.rawList[index].ratio = 0
      } else {
        this.rawList[index].ratio = Number(val).toFixed(2)
      }
      // 清除该索引的错误
      delete this.ratioErrors[index]
    },

    validateRatio(index) {
      const ratio = Number(this.rawList[index].ratio) || 0
      if (ratio < 0 || ratio > 100) {
        this.ratioErrors[index] = '比例必须在0-100%之间'
        return false
      }
      delete this.ratioErrors[index]
      return true
    },

    handleDelete(index) {
      if (this.rawList.length <= 2) {
        this.$message.warning('至少需要2个原料')
        return
      }
      this.rawList.splice(index, 1)
      // 重新分配比例，使总和为100%
      this.redistributeRatios()
    },

    redistributeRatios() {
      if (this.rawList.length === 0) return
      
      const avgRatio = 100 / this.rawList.length
      this.rawList.forEach((item, index) => {
        // 最后一个使用剩余比例，确保总和为100%
        if (index === this.rawList.length - 1) {
          const sum = this.rawList.slice(0, -1).reduce((s, r) => s + (Number(r.ratio) || 0), 0)
          item.ratio = (100 - sum).toFixed(2)
        } else {
          item.ratio = avgRatio.toFixed(2)
        }
      })
    },

    async handleOk() {
      // 最终验证
      if (!this.validateBagName()) {
        return
      }

      // 验证所有比例
      let hasError = false
      this.rawList.forEach((item, index) => {
        const ratio = Number(item.ratio) || 0
        if (ratio < 0 || ratio > 100) {
          this.ratioErrors[index] = '比例必须在0-100%之间'
          hasError = true
        }
      })

      if (hasError) {
        this.$message.error('请修正比例错误')
        return
      }

      if (this.totalRatio !== 100) {
        this.$message.error('比例总和必须等于100%')
        return
      }

      if (this.rawList.length < 2 || this.rawList.length > 3) {
        this.$message.error('原料数量必须在2-3个之间')
        return
      }

      this.loading = true
      try {
        const params = {
          formulaId: this.formulaId,
          rawBagName: this.formData.bagName.trim(),
          formulaRawBagDetailList: this.rawList.map((item, index) => ({
            rawId: item.rawId,
            percentage: Number(item.ratio),
            isMajor: index === this.selectedRowKey ? 1 : 0
          }))
        }

        const res = await post('/nhf-app/formularawbag/addFormulaRawBag', params)
        
        if (res && (res.data !== false)) {
          this.$message.success('创建原料组合包成功')
          // 通知父组件刷新原料列表
          this.$emit('refresh')
          this.$emit('success')
          this.handleCancel()
        } else {
          this.$message.error('原料已关联绑定配方中多个组合包，请解绑后再打包。')
        }
      } catch (error) {
        this.$message.error(error?.msg || '创建失败，请重试')
      } finally {
        this.loading = false
      }
    },

    handleCancel() {
      this.$emit('close')
    }
  }
}
</script>

<style lang="scss" scoped>
.create-raw-bag-container {
  .ratio-summary {
    text-align: right;
    font-size: 14px;
  }

  .bag-name-form-item {
    margin-bottom: 12px;

    ::v-deep .ant-form-item-label {
      padding-bottom: 0;
      line-height: 32px;
    }

    ::v-deep .ant-form-item-control {
      line-height: 32px;
    }
  }

  .bag-name-input {
    max-width: 380px;
  }
}

::v-deep .ant-table-thead > tr > th {
  background-color: #fafafa;
  font-weight: 600;
}

::v-deep .ant-table-tbody > tr > td {
  border-bottom: 1px solid #f0f0f0;
}
</style>

